import { useEffect, useMemo, useState, useCallback } from "react";
import { useParams } from "react-router-dom";
import { Download, Copy, RefreshCw, Sparkles, Users, UserCheck, UserX, UserPlus, MessageSquare, Search, Trophy, TrendingUp, Eye, Check, X as XIcon, Clock, AlertTriangle, FileImage, CheckSquare, Undo2, Pencil, Star, StickyNote, ShieldCheck, Trash2 } from "lucide-react";
import { useI18n } from "../i18n/I18nContext";
import LanguageToggle from "../components/LanguageToggle";
import AdminShell from "../components/AdminShell";
import LicenseReviewDrawer from "../components/LicenseReviewDrawer";
import AddWalkInModal from "../components/AddWalkInModal";
import EditRegistrationDrawer from "../components/EditRegistrationDrawer";
import { api, csvDownloadUrl, vipRosterDownloadUrl } from "../lib/nailApi";

const TOKEN_KEY = "nailEvent.adminToken";

export default function NailEventAdmin() {
  const { slug } = useParams();
  const { lang, t } = useI18n();

  // Phase 8.10.1 — accept JWT session from /admin/login (localStorage.nailViet.adminJWT).
  // If a JWT exists, treat this page as authenticated (backend prefers JWT over
  // any legacy X-Admin-Token). Otherwise fall back to the internal token login
  // for backwards compatibility; if that's also empty, redirect to /admin/login.
  const [token, setToken] = useState(() => {
    const legacy = sessionStorage.getItem(TOKEN_KEY);
    if (legacy) return legacy;
    if (typeof window !== "undefined" && localStorage.getItem("nailViet.adminJWT")) return "jwt";
    return "";
  });
  const [tokenDraft, setTokenDraft] = useState("");
  const [loginError, setLoginError] = useState("");

  const [stats, setStats] = useState(null);
  const [campaigns, setCampaigns] = useState([]);
  const [rows, setRows] = useState([]);
  const [query, setQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [sourceFilter, setSourceFilter] = useState("");
  const [busy, setBusy] = useState(false);
  const [toast, setToast] = useState(null);
  const [reviewOpen, setReviewOpen] = useState(null);   // registration row to review
  const [showTestData, setShowTestData] = useState(false);
  // Phase 8.12 — Aug 09 event-day admin toolkit
  const [showAddWalkin, setShowAddWalkin] = useState(false);
  const [editingReg, setEditingReg] = useState(null);
  // Jul 2026 — bulk check-in ("Chọn nhiều") mode
  const BULK_CAP = 10;
  const [bulkMode, setBulkMode] = useState(false);
  const [selected, setSelected] = useState(() => new Set());
  const [bulkBusy, setBulkBusy] = useState(false);

  const headers = useMemo(() => ({ "X-Admin-Token": token }), [token]);

  const flash = useCallback((type, text) => {
    // Coerce FastAPI 422 validation-error arrays into a readable string so that
    // React never tries to render an object array as a child (Phase 8.13 hotfix).
    let msg = text;
    if (Array.isArray(msg)) {
      msg = msg.map((d) => (d && typeof d === "object" ? (d.msg || JSON.stringify(d)) : String(d))).join("; ");
    } else if (msg && typeof msg === "object") {
      msg = msg.msg || msg.detail || JSON.stringify(msg);
    } else if (msg == null) {
      msg = "Error";
    }
    setToast({ type, text: String(msg) });
    setTimeout(() => setToast(null), 2500);
  }, []);

  const loadAll = useCallback(async () => {
    if (!token) return;
    setBusy(true);
    try {
      const [s, c, r] = await Promise.all([
        api.get(`/admin/nail-events/${slug}/stats`, { headers }),
        api.get(`/admin/nail-events/${slug}/campaigns`, { headers }).catch(() => ({ data: { campaigns: [] } })),
        api.get(`/admin/nail-events/${slug}/registrations`, {
          headers,
          params: {
            q: query || undefined,
            status: statusFilter === "all" ? undefined : statusFilter,
            source: sourceFilter || undefined,
            include_test: showTestData ? true : undefined,
            limit: 500,
          },
        }),
      ]);
      setStats(s.data);
      setCampaigns(c.data?.campaigns || []);
      setRows(r.data.registrations);
    } catch (err) {
      if (err?.response?.status === 401) {
        setToken("");
        sessionStorage.removeItem(TOKEN_KEY);
        setLoginError(t("admin.login_error"));
      } else {
        flash("error", err?.response?.data?.detail || "Error");
      }
    } finally {
      setBusy(false);
    }
  }, [token, slug, headers, query, statusFilter, sourceFilter, showTestData, t, flash]);

  useEffect(() => {
    if (token) {
      const id = setTimeout(loadAll, 250);
      return () => clearTimeout(id);
    }
  }, [token, loadAll]);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoginError("");
    try {
      await api.get(`/admin/nail-events/${slug}/stats`, { headers: { "X-Admin-Token": tokenDraft } });
      setToken(tokenDraft);
      sessionStorage.setItem(TOKEN_KEY, tokenDraft);
    } catch (err) {
      setLoginError(err?.response?.status === 401 ? t("admin.login_error") : err?.message || "Error");
    }
  };

  const handleResend = async (id) => {
    try {
      const r = await api.post(`/admin/nail-events/${slug}/registrations/${id}/resend-sms`, {}, { headers });
      flash(r.data.status === "sent" ? "success" : "warn", `SMS: ${r.data.status}`);
      // Phase 8.18 — optimistic green-pill update. Preview will resolve to
      // status='skipped' (dry-run) but the pill still turns green because
      // that's a real "attempt happened just now" signal for the operator.
      const now = new Date().toISOString();
      setRows((prev) => prev.map((row) => row.id === id ? { ...row, last_sms_sent_at: now } : row));
    } catch (err) {
      flash("error", err?.response?.data?.detail || "Error");
    }
  };

  const handleDelete = async (reg) => {
    const label = reg.business_name || reg.owner_name || `#${reg.id}`;
    const msg = lang === "en"
      ? `Delete registration for "${label}"? This cannot be undone.`
      : `Xoá đăng ký cho "${label}"? Không thể khôi phục.`;
    if (!window.confirm(msg)) return;
    try {
      await api.delete(`/admin/nail-events/${slug}/registrations/${reg.id}`, { headers });
      setRows((prev) => prev.filter((row) => row.id !== reg.id));
      flash("success", lang === "en" ? "Deleted" : "Đã xoá");
    } catch (err) {
      flash("error", err?.response?.data?.detail || "Error");
    }
  };

  // Jul 2026 — {n} interpolation helper for bulk/undo copy
  const ti = (key, vars = {}) => {
    const s = t(key);
    return Object.keys(vars).reduce((acc, k) => acc.replace(new RegExp(`\\{${k}\\}`, "g"), vars[k]), s);
  };

  const toggleSelected = (id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else if (next.size < BULK_CAP) next.add(id);
      return next;
    });
  };

  const exitBulkMode = () => {
    setBulkMode(false);
    setSelected(new Set());
  };

  const handleBulkCheckin = async () => {
    if (selected.size === 0) return;
    if (!window.confirm(ti("admin.bulk_confirm", { n: selected.size }))) return;
    setBulkBusy(true);
    try {
      const ids = Array.from(selected);
      const r = await api.post(
        `/admin/nail-events/${slug}/registrations/bulk-checkin`,
        { reg_ids: ids, checked_in_by: "admin" },
        { headers },
      );
      flash("success", ti("admin.bulk_success", { n: r.data?.checked_in ?? 0, requested: r.data?.requested ?? ids.length }));
      exitBulkMode();
      await loadAll();
    } catch (err) {
      flash("error", err?.response?.data?.detail || "Error");
    } finally {
      setBulkBusy(false);
    }
  };

  // Phase 8.13.B — Bulk SMS resend. Only fires to VERIFIED phones (B2 rule).
  const handleBulkSms = async () => {
    if (selected.size === 0) return;
    const ids = Array.from(selected);
    // Client-side warn about verified filter
    const selectedRows = rows.filter((r) => selected.has(r.id));
    const verifiedIds = selectedRows.filter((r) => r.is_verified).map((r) => r.id);
    const skippedUnverified = selectedRows.length - verifiedIds.length;
    if (verifiedIds.length === 0) {
      flash("error", lang === "en"
        ? "None of the selected have a verified phone. SMS not sent."
        : "Không có số nào đã xác minh. Không gửi SMS.");
      return;
    }
    const msg = lang === "en"
      ? `Send confirmation SMS to ${verifiedIds.length} verified? (${skippedUnverified} unverified will be skipped)`
      : `Gửi lại SMS xác nhận cho ${verifiedIds.length} số đã xác minh? (${skippedUnverified} số chưa xác minh sẽ bị bỏ qua)`;
    if (!window.confirm(msg)) return;
    setBulkBusy(true);
    try {
      const r = await api.post(
        `/admin/nail-events/${slug}/registrations/bulk-resend-sms`,
        { reg_ids: verifiedIds },
        { headers },
      );
      flash("success", lang === "en"
        ? `SMS sent to ${r.data?.sent || 0}. ${r.data?.failed || 0} failed.`
        : `Đã gửi SMS tới ${r.data?.sent || 0} số. Lỗi: ${r.data?.failed || 0}.`);
      exitBulkMode();
    } catch (err) {
      flash("error", err?.response?.data?.detail || "Error");
    } finally {
      setBulkBusy(false);
    }
  };

  // Phase 8.13.B — Bulk CSV export of just the selected rows.
  const handleBulkExport = () => {
    if (selected.size === 0) return;
    const selectedRows = rows.filter((r) => selected.has(r.id));
    // Header
    const cols = ["id", "business_name", "owner_name", "phone", "email",
                  "preferred_language", "guest_count", "checked_in", "is_walkin",
                  "is_verified", "is_vip", "admin_notes", "created_at"];
    const escape = (v) => {
      const s = v == null ? "" : String(v);
      if (/[",\n]/.test(s)) return '"' + s.replace(/"/g, '""') + '"';
      return s;
    };
    const lines = [cols.join(",")];
    for (const r of selectedRows) {
      lines.push(cols.map((c) => escape(r[c])).join(","));
    }
    const blob = new Blob(["\uFEFF" + lines.join("\r\n")], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${slug}-selected-${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    flash("success", lang === "en" ? `Exported ${selectedRows.length} rows` : `Đã xuất ${selectedRows.length} dòng`);
  };

  const handleUndoCheckin = async (id) => {
    if (!window.confirm(t("admin.undo_confirm"))) return;
    try {
      await api.post(`/admin/nail-events/${slug}/registrations/${id}/undo-checkin`, {}, { headers });
      flash("success", t("admin.undo_success"));
      await loadAll();
    } catch (err) {
      flash("error", err?.response?.data?.detail || "Error");
    }
  };

  const copyLink = async (path, label) => {
    const url = `${window.location.origin}${path}`;
    try {
      await navigator.clipboard.writeText(url);
      flash("success", `${label}: ${t("admin.copied")}`);
    } catch {
      window.prompt(label, url);
    }
  };

  // Phase 8.4 — filter obvious test rows from view unless toggled on
  const visibleRows = useMemo(() => {
    let base = rows;
    if (!showTestData) {
      const TEST_RE = /test|@x\.com|@y\.com|example\.com|555\d{7}|Salon \d+$|Salon [1-9]$|Agent Toan|Updated$/i;
      base = rows.filter((r) => {
        const hay = `${r.business_name || ""} ${r.owner_name || ""} ${r.email || ""} ${r.phone || ""}`;
        return !TEST_RE.test(hay);
      });
    }
    // Phase 8.13 (B3) — client-side verified/unverified filter chip
    if (statusFilter === "verified") return base.filter((r) => r.is_verified);
    if (statusFilter === "unverified") return base.filter((r) => !r.is_verified);
    return base;
  }, [rows, showTestData, statusFilter]);

  // Phase 8.4 — sort so the amber (to-do) states float to the top.
  // Jul 2026: when the "Chờ duyệt hồ sơ" chip is active, the backend already
  // orders oldest-first (longest-waiting person on top); don't re-sort here.
  const sortedRows = useMemo(() => {
    if (statusFilter === "pending_review") return visibleRows;
    const rank = (r) => {
      const s = r.license_verification || {};
      const admin = s.admin_status;
      const ai = s.ai_status;
      if (!s.id) return 99;                                     // no upload → bottom
      if (admin === "admin_approved" || admin === "admin_rejected") return 20;
      if (admin === "awaiting_member") return 10;                // waiting on member
      if (ai === "processing") return 5;                         // in progress
      return 0;                                                  // amber to-do
    };
    return [...visibleRows].sort((a, b) => rank(a) - rank(b));
  }, [visibleRows, statusFilter]);

  const sourceOptions = useMemo(() => {
    const list = stats?.sources || [];
    return list.map((s) => s.source).filter((s) => s && s !== "direct");
  }, [stats]);

  if (!token) {
    return <AdminLogin />;
  }

  return (
    <AdminShell
      testid="admin-page"
      subtitle={t("admin.title")}
      title={stats?.event?.name || "—"}
      backTo="/admin/nail-events"
      actions={
        <>
          <button
            type="button"
            data-testid="copy-public-link"
            onClick={() => copyLink(`/nail-event/${slug}`, t("admin.copy_public"))}
            className="hidden md:inline-flex items-center gap-1.5 border border-espresso/20 text-espresso px-3.5 py-2 rounded-full text-xs font-medium hover:bg-linen-200 transition-all"
          >
            <Copy className="w-3.5 h-3.5" /> {t("admin.copy_public")}
          </button>
          <button
            type="button"
            data-testid="copy-checkin-link"
            onClick={() => copyLink(`/nail-event/${slug}/checkin`, t("admin.copy_checkin"))}
            className="hidden md:inline-flex items-center gap-1.5 border border-espresso/20 text-espresso px-3.5 py-2 rounded-full text-xs font-medium hover:bg-linen-200 transition-all"
          >
            <Copy className="w-3.5 h-3.5" /> {t("admin.copy_checkin")}
          </button>
          <a
            href={csvDownloadUrl(slug, token)}
            data-testid="admin-export-csv-button"
            className="inline-flex items-center gap-1.5 bg-espresso text-linen px-3.5 py-2 rounded-full text-xs font-medium hover:bg-espresso-hover transition-all"
          >
            <Download className="w-3.5 h-3.5" /> {t("admin.export_csv")}
          </a>
          <a
            href={vipRosterDownloadUrl(slug, token)}
            data-testid="admin-export-vip-roster-button"
            className="inline-flex items-center gap-1.5 bg-amber-300 text-espresso px-3.5 py-2 rounded-full text-xs font-semibold hover:bg-amber-200 transition-all"
            title="Xuất danh sách VIP để tổ chức viên chuẩn bị đón"
          >
            <Download className="w-3.5 h-3.5" /> VIP roster
          </a>
        </>
      }
    >
      <div className="max-w-[1400px]">
        {/* Stats grid */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3 md:gap-4">
          <StatCard icon={Users} label={t("admin.stats_total")} value={stats?.total_signups ?? "—"} testId="stat-total" />
          <StatCard icon={UserCheck} label={t("admin.stats_checked_in")} value={stats?.checked_in ?? "—"} testId="stat-checked-in" tone="success" />
          <StatCard icon={UserX} label={t("admin.stats_not_checked_in")} value={stats?.not_checked_in ?? "—"} testId="stat-not-checked-in" tone="warn" />
          <StatCard icon={UserPlus} label={t("admin.stats_walkins")} value={stats?.walkins ?? "—"} testId="stat-walkins" />
          <StatCard icon={MessageSquare} label={t("admin.stats_sms")} value={stats?.sms_sent ?? "—"} testId="stat-sms" />
        </div>

        {/* Top Campaigns */}
        <TopCampaigns campaigns={campaigns} lang={lang} />

        {/* Filters */}
        <div className="mt-8 bg-white border border-espresso/10 rounded-2xl p-4 flex flex-wrap items-center gap-3">
          <div className="relative flex-1 min-w-[240px]">
            <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-espresso-muted" />
            <input
              data-testid="admin-search-input"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder={t("admin.search_placeholder")}
              className="w-full bg-white border border-espresso/10 rounded-full pl-10 pr-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-espresso"
            />
          </div>
          <Pill active={statusFilter === "all"} onClick={() => setStatusFilter("all")} testId="filter-all">{t("admin.filter_all")}</Pill>
          <Pill active={statusFilter === "checked_in"} onClick={() => setStatusFilter("checked_in")} testId="filter-checked-in">{t("admin.filter_checked_in")}</Pill>
          <Pill active={statusFilter === "not_checked_in"} onClick={() => setStatusFilter("not_checked_in")} testId="filter-not-checked-in">{t("admin.filter_not_checked_in")}</Pill>
          <Pill active={statusFilter === "walkin"} onClick={() => setStatusFilter("walkin")} testId="filter-walkin">{t("admin.filter_walkin")}</Pill>
          {/* Phase 8.13 (B3) — Verified vs Unverified phone filter chips */}
          <Pill
            active={statusFilter === "verified"}
            onClick={() => setStatusFilter("verified")}
            testId="filter-verified"
          >
            {lang === "en" ? "Verified" : "Đã xác minh"}
          </Pill>
          <Pill
            active={statusFilter === "unverified"}
            onClick={() => setStatusFilter("unverified")}
            testId="filter-unverified"
            badge={stats?.unverified_count > 0 ? stats.unverified_count : null}
          >
            {lang === "en" ? "Unverified" : "Chưa xác minh"}
          </Pill>
          <Pill
            active={statusFilter === "pending_review"}
            onClick={() => setStatusFilter("pending_review")}
            testId="filter-pending-review"
            badge={stats?.pending_review_count > 0 ? stats.pending_review_count : null}
          >
            {t("admin.filter_pending_review")}
          </Pill>
          <select
            data-testid="filter-source"
            value={sourceFilter}
            onChange={(e) => setSourceFilter(e.target.value)}
            className="bg-white border border-espresso/10 rounded-full px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-espresso"
          >
            <option value="">{t("admin.source_all")}</option>
            {sourceOptions.map((s) => (
              <option key={s} value={s}>{s}</option>
            ))}
          </select>
          <button
            type="button"
            onClick={() => setShowAddWalkin(true)}
            data-testid="admin-add-walkin-button"
            className="inline-flex items-center gap-1.5 bg-emerald2 text-white rounded-full text-xs font-semibold px-4 py-2 hover:bg-emerald2-dark transition shadow-sm"
          >
            <UserPlus className="w-3.5 h-3.5" />
            {lang === "en" ? "Add Walk-in" : "Thêm Walk-in"}
          </button>
          <button
            type="button"
            onClick={loadAll}
            disabled={busy}
            data-testid="admin-refresh"
            className="inline-flex items-center gap-1.5 text-xs font-medium text-espresso-soft hover:text-espresso px-3 py-2"
          >
            <RefreshCw className={`w-3.5 h-3.5 ${busy ? "animate-spin" : ""}`} />
          </button>
          <button
            type="button"
            onClick={() => (bulkMode ? exitBulkMode() : setBulkMode(true))}
            data-testid="bulk-mode-toggle"
            className={`inline-flex items-center gap-1.5 rounded-full text-xs font-medium px-3.5 py-2 transition-all ${
              bulkMode
                ? "bg-espresso text-linen"
                : "border border-espresso/15 text-espresso-soft hover:text-espresso hover:bg-linen-200"
            }`}
          >
            <CheckSquare className="w-3.5 h-3.5" />
            {bulkMode ? t("admin.bulk_mode_exit") : t("admin.bulk_mode_toggle")}
          </button>
          <label className="ml-auto inline-flex items-center gap-2 text-xs text-espresso-soft cursor-pointer" data-testid="show-test-toggle-wrap">
            <input
              type="checkbox"
              checked={showTestData}
              onChange={(e) => setShowTestData(e.target.checked)}
              data-testid="show-test-toggle"
              className="rounded border-espresso/30"
            />
            {lang === "en" ? "Show test data" : "Hiện dữ liệu test"}
          </label>
        </div>

        {toast && (
          <div
            data-testid="admin-toast"
            className={`mt-4 rounded-xl px-4 py-2.5 text-sm font-medium inline-block ${
              toast.type === "success"
                ? "bg-[#E1EDE6] text-[#2D4A3E]"
                : toast.type === "warn"
                  ? "bg-[#FCEBD4] text-[#8A5A19]"
                  : "bg-[#F5DADA] text-[#7A2E2E]"
            }`}
          >
            {toast.text}
          </div>
        )}

        {/* Table */}
        <div className="mt-6 bg-white border border-espresso/10 rounded-2xl overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-[10px] uppercase tracking-[0.22em] text-espresso-muted bg-linen-100">
                  {bulkMode && <Th><span className="sr-only">Select</span></Th>}
                  <Th>{t("admin.table_business")}</Th>
                  <Th>{t("admin.table_owner")}</Th>
                  <Th>{t("admin.table_phone")}</Th>
                  <Th>{t("admin.table_guests")}</Th>
                  <Th>{t("admin.table_lang")}</Th>
                  <Th>{t("admin.table_source")}</Th>
                  <Th>{t("admin.table_document") || "Giấy tờ"}</Th>
                  <Th>{t("admin.table_status")}</Th>
                  <Th>{t("admin.table_actions")}</Th>
                </tr>
              </thead>
              <tbody>
                {sortedRows.length === 0 && !busy && (
                  <tr><td colSpan={bulkMode ? 10 : 9} className="text-center py-12 text-espresso-muted">{t("admin.no_registrations")}</td></tr>
                )}
                {sortedRows.map((r) => {
                  const isSelected = selected.has(r.id);
                  const canSelect = !r.checked_in && (isSelected || selected.size < BULK_CAP);
                  return (
                  <tr
                    key={r.id}
                    data-testid={`admin-row-${r.id}`}
                    className={`border-t border-espresso/5 transition-colors ${isSelected ? "bg-emerald2/5" : "hover:bg-linen-100/60"}`}
                  >
                    {bulkMode && (
                      <td className="px-4 py-3">
                        <input
                          type="checkbox"
                          checked={isSelected}
                          disabled={!canSelect}
                          onChange={() => toggleSelected(r.id)}
                          aria-label={`Select ${r.business_name}`}
                          data-testid={`bulk-select-${r.id}`}
                          className="w-4 h-4 rounded border-espresso/40 accent-espresso disabled:opacity-40"
                        />
                      </td>
                    )}
                    <td className="px-4 py-3 font-medium">
                      <div className="flex items-center gap-1.5">
                        {r.is_vip && (
                          <span
                            data-testid={`vip-badge-${r.id}`}
                            title={lang === "en" ? "VIP guest" : "Khách VIP"}
                            className="inline-flex items-center gap-0.5 rounded-full bg-gold-100 text-gold-700 border border-gold-300 px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider"
                          >
                            <Star className="w-2.5 h-2.5 fill-current" /> VIP
                          </span>
                        )}
                        <span>{r.business_name}</span>
                        {r.admin_notes && (
                          <span
                            data-testid={`note-indicator-${r.id}`}
                            title={r.admin_notes}
                            className="inline-flex items-center rounded-full bg-linen-200 text-espresso-soft px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-wider"
                          >
                            <StickyNote className="w-2.5 h-2.5" />
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-espresso-soft">{r.owner_name || "—"}</td>
                    <td className="px-4 py-3 font-mono text-xs">
                      <div className="flex items-center gap-1.5">
                        <span>{r.phone}</span>
                        {r.is_verified ? (
                          <span
                            data-testid={`verified-badge-${r.id}`}
                            title={lang === "en" ? "Phone verified via OTP" : "Số điện thoại đã xác minh qua OTP"}
                            className="inline-flex items-center gap-0.5 rounded-full bg-emerald2/10 text-emerald2 border border-emerald2/30 px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider whitespace-nowrap"
                          >
                            <ShieldCheck className="w-2.5 h-2.5" /> {lang === "en" ? "Verified" : "Đã XM"}
                          </span>
                        ) : (
                          <span
                            data-testid={`unverified-badge-${r.id}`}
                            title={lang === "en" ? "Phone not verified via OTP" : "Chưa xác minh qua OTP"}
                            className="inline-flex items-center gap-0.5 rounded-full bg-amber-100 text-amber-800 border border-amber-300 px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider whitespace-nowrap"
                          >
                            <AlertTriangle className="w-2.5 h-2.5" /> {lang === "en" ? "Unverified" : "Chưa XM"}
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-3">{r.guest_count ?? "—"}</td>
                    <td className="px-4 py-3 uppercase text-xs">{r.preferred_language}</td>
                    <td className="px-4 py-3 text-espresso-soft">{r.source || "direct"}</td>
                    <td className="px-4 py-3">
                      <DocumentGlyph registration={r} onOpen={() => setReviewOpen(r)} />
                    </td>
                    <td className="px-4 py-3">
                      <StatusPill r={r} />
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2 flex-wrap">
                        <button
                          type="button"
                          onClick={() => setEditingReg(r)}
                          data-testid={`edit-registration-${r.id}`}
                          title={lang === "en" ? "Edit registration" : "Sửa đăng ký"}
                          className="inline-flex items-center gap-1 text-espresso-soft hover:text-espresso text-xs font-medium border border-espresso/15 rounded-full px-3 py-1.5"
                        >
                          <Pencil className="w-3.5 h-3.5" />
                          <span className="hidden sm:inline">{lang === "en" ? "Edit" : "Sửa"}</span>
                        </button>
                        {(() => {
                          // Phase 8.18 — Resend SMS pill turns green when this
                          // registration was texted in the last 24h (or just
                          // clicked). Reads last_sms_sent_at (ISO string).
                          const smsRecent = (() => {
                            if (!r.last_sms_sent_at) return false;
                            const t = Date.parse(r.last_sms_sent_at);
                            if (Number.isNaN(t)) return false;
                            return (Date.now() - t) < 24 * 60 * 60 * 1000;
                          })();
                          return (
                            <button
                              type="button"
                              onClick={() => handleResend(r.id)}
                              data-testid={`resend-sms-${r.id}`}
                              data-sms-recent={smsRecent ? "1" : "0"}
                              title={smsRecent
                                ? (lang === "en" ? "SMS sent within the last 24h" : "SMS đã gửi trong 24 giờ qua")
                                : t("admin.resend_sms")}
                              className={`inline-flex items-center gap-1 text-xs font-medium rounded-full px-3 py-1.5 transition ${
                                smsRecent
                                  ? "bg-emerald-100 text-emerald-800 border border-emerald-300 hover:bg-emerald-200"
                                  : "text-espresso-soft hover:text-espresso border border-espresso/15"
                              }`}
                            >
                              <MessageSquare className="w-3.5 h-3.5" /> {t("admin.resend_sms")}
                              {smsRecent && <span className="ml-0.5">✓</span>}
                            </button>
                          );
                        })()}
                        {r.checked_in && (
                          <button
                            type="button"
                            onClick={() => handleUndoCheckin(r.id)}
                            data-testid={`undo-checkin-${r.id}`}
                            title={t("admin.undo_checkin")}
                            className="inline-flex items-center gap-1 text-espresso-soft hover:text-espresso text-xs font-medium border border-espresso/15 rounded-full px-3 py-1.5"
                          >
                            <Undo2 className="w-3.5 h-3.5" /> {t("admin.undo_checkin")}
                          </button>
                        )}
                        {/* Phase 8.18 — Delete bogus signups. Confirm dialog
                            in handleDelete prevents accidental clicks. */}
                        <button
                          type="button"
                          onClick={() => handleDelete(r)}
                          data-testid={`delete-registration-${r.id}`}
                          title={lang === "en" ? "Delete registration" : "Xoá đăng ký"}
                          className="inline-flex items-center gap-1 text-red-600 hover:text-red-700 text-xs font-medium border border-red-200 hover:border-red-300 hover:bg-red-50 rounded-full px-3 py-1.5"
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                          <span className="hidden sm:inline">{lang === "en" ? "Delete" : "Xoá"}</span>
                        </button>
                      </div>
                    </td>
                  </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Phase 8.4 — Document review drawer */}
      {reviewOpen && (
        <LicenseReviewDrawer
          registration={reviewOpen}
          onClose={() => setReviewOpen(null)}
          onChanged={loadAll}
          lang={lang}
        />
      )}

      {/* Phase 8.12.1 — Add Walk-in modal */}
      {showAddWalkin && (
        <AddWalkInModal
          slug={slug}
          lang={lang}
          checkinPin={stats?.event?.checkin_pin || stats?.checkin_pin || "2468"}
          onClose={() => setShowAddWalkin(false)}
          onCreated={(data) => {
            flash("success", lang === "en"
              ? `Walk-in created: ${data?.business_name || ""}`
              : `Đã thêm walk-in: ${data?.business_name || ""}`);
            loadAll();
          }}
        />
      )}

      {/* Phase 8.12.2 — Edit Registration drawer */}
      {editingReg && (
        <EditRegistrationDrawer
          slug={slug}
          registration={editingReg}
          lang={lang}
          onClose={() => setEditingReg(null)}
          onSaved={() => { setEditingReg(null); loadAll(); flash("success", lang === "en" ? "Saved" : "Đã lưu"); }}
        />
      )}

      {/* Jul 2026 — Floating bulk check-in action bar (mobile-friendly at 375px) */}
      {bulkMode && (
        <div
          data-testid="bulk-action-bar"
          className="fixed inset-x-0 bottom-0 z-40 border-t border-espresso/15 bg-white/95 backdrop-blur px-4 py-3 shadow-[0_-14px_28px_-14px_rgba(44,36,27,0.35)]"
        >
          <div className="max-w-[1400px] mx-auto flex items-center justify-between gap-3">
            <div className="min-w-0">
              <div data-testid="bulk-selected-count" className="text-sm font-semibold text-espresso">
                {ti("admin.bulk_count", { n: selected.size })}
              </div>
              <div className="text-[10px] uppercase tracking-[0.22em] text-espresso-muted mt-0.5">
                {t("admin.bulk_cap_notice")}
              </div>
            </div>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={exitBulkMode}
                data-testid="bulk-cancel"
                className="text-xs font-medium text-espresso-soft hover:text-espresso border border-espresso/15 rounded-full px-4 py-2"
              >
                {t("admin.bulk_mode_exit")}
              </button>
              <button
                type="button"
                onClick={handleBulkExport}
                disabled={selected.size === 0}
                data-testid="bulk-export-submit"
                className="inline-flex items-center gap-1.5 border border-espresso/15 text-espresso rounded-full px-4 py-2 text-xs font-semibold disabled:opacity-40 hover:bg-linen-100 transition-all"
              >
                <Download className="w-3.5 h-3.5" />
                {lang === "en" ? `Export ${selected.size} CSV` : `Xuất ${selected.size} CSV`}
              </button>
              <button
                type="button"
                onClick={handleBulkSms}
                disabled={selected.size === 0 || bulkBusy}
                data-testid="bulk-sms-submit"
                className="inline-flex items-center gap-1.5 border border-espresso/15 text-espresso rounded-full px-4 py-2 text-xs font-semibold disabled:opacity-40 hover:bg-linen-100 transition-all"
              >
                <MessageSquare className="w-3.5 h-3.5" />
                {lang === "en" ? `Resend SMS (${selected.size})` : `Gửi lại SMS (${selected.size})`}
              </button>
              <button
                type="button"
                onClick={handleBulkCheckin}
                disabled={selected.size === 0 || bulkBusy}
                data-testid="bulk-checkin-submit"
                className="inline-flex items-center gap-1.5 bg-emerald2 text-white rounded-full px-4 py-2 text-xs font-semibold disabled:opacity-40 disabled:cursor-not-allowed hover:bg-emerald2/90 transition-all"
              >
                <UserCheck className="w-3.5 h-3.5" />
                {ti("admin.bulk_checkin_action", { n: selected.size })}
              </button>
            </div>
          </div>
        </div>
      )}
    </AdminShell>
  );
}

function AdminLogin() {
  const { t } = useI18n();
  const currentPath = typeof window !== "undefined" ? (window.location.pathname + window.location.search) : "/admin";
  const loginHref = `/admin/login?next=${encodeURIComponent(currentPath)}`;
  return (
    <div className="min-h-screen bg-linen flex items-center justify-center px-6">
      <div className="absolute top-6 right-6"><LanguageToggle /></div>
      <div
        data-testid="admin-legacy-login-redirect"
        className="w-full max-w-md bg-white border border-espresso/10 rounded-3xl p-8 md:p-10 shadow-[0_24px_60px_-30px_rgba(44,36,27,0.35)]"
      >
        <div className="flex items-center gap-2 mb-5">
          <Sparkles className="w-4 h-4 text-rose-accent" />
          <span className="text-[11px] uppercase tracking-[0.28em] text-espresso-muted font-semibold">
            {t("admin.title")}
          </span>
        </div>
        <h2 className="font-serif text-3xl md:text-4xl text-espresso">{t("admin.login_title")}</h2>
        <p className="text-espresso-soft mt-2">{t("admin.login_subtitle")}</p>
        <a
          href={loginHref}
          data-testid="admin-login-submit"
          className="mt-6 inline-flex items-center justify-center w-full bg-espresso text-linen rounded-full px-6 py-4 font-medium hover:bg-espresso-hover transition-all"
        >
          {t("admin.login_submit")}
        </a>
      </div>
    </div>
  );
}

function StatCard({ icon: Icon, label, value, tone, testId }) {
  const toneCls =
    tone === "success" ? "text-[#2D4A3E]" : tone === "warn" ? "text-[#8A5A19]" : "text-espresso";
  return (
    <div className="bg-white border border-espresso/10 rounded-2xl p-4 md:p-5" data-testid={testId}>
      <div className="flex items-center justify-between">
        <span className="text-[10px] uppercase tracking-[0.22em] text-espresso-muted font-semibold">{label}</span>
        <Icon className={`w-4 h-4 ${toneCls}`} />
      </div>
      <div className={`font-serif text-4xl md:text-5xl mt-2 ${toneCls}`}>{value}</div>
    </div>
  );
}

function Pill({ active, onClick, children, testId, badge }) {
  return (
    <button
      type="button"
      data-testid={testId}
      onClick={onClick}
      className={`inline-flex items-center gap-1.5 px-3.5 py-2 rounded-full text-xs font-medium transition-all ${
        active ? "bg-espresso text-linen" : "border border-espresso/15 text-espresso-soft hover:text-espresso hover:bg-linen-200"
      }`}
    >
      {children}
      {badge != null && (
        <span
          data-testid={`${testId}-badge`}
          className={`inline-flex items-center justify-center min-w-[1.25rem] h-5 px-1.5 rounded-full text-[10px] font-semibold ${
            active ? "bg-linen text-espresso" : "bg-amber-400 text-espresso"
          }`}
        >
          {badge}
        </span>
      )}
    </button>
  );
}

function StatusPill({ r }) {
  const { t } = useI18n();
  if (r.checked_in) {
    return (
      <span className="inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-[10px] font-semibold uppercase tracking-wider bg-[#E1EDE6] text-[#2D4A3E]">
        {t("checkin.checked_in_badge")}
      </span>
    );
  }
  if (r.is_walkin) {
    return (
      <span className="inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-[10px] font-semibold uppercase tracking-wider bg-[#F1E1DC] text-[#7A4D40]">
        {t("checkin.walkin_badge")}
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-[10px] font-semibold uppercase tracking-wider bg-[#EAE5D9] text-espresso">
      {t("checkin.registered_badge")}
    </span>
  );
}

function Th({ children }) {
  return <th className="px-4 py-3 font-semibold">{children}</th>;
}

// Channel theme — keeps colors consistent with the platform palette
const CAMPAIGN_THEME = {
  facebook:    { color: "#1877F2", dot: "#1877F2" },
  instagram:   { color: "#E1306C", dot: "#E1306C" },
  tiktok:      { color: "#000000", dot: "#000000" },
  google:      { color: "#4285F4", dot: "#4285F4" },
  "qr-code":   { color: "#0E5B4F", dot: "#0E5B4F" },
  sms:         { color: "#0E5B4F", dot: "#147A6B" },
  email:       { color: "#C9A14A", dot: "#C9A14A" },
  agent:       { color: "#C66A2B", dot: "#C66A2B" },
  flyer:       { color: "#8B887F", dot: "#8B887F" },
  manual:      { color: "#5C5A55", dot: "#5C5A55" },
  unknown:     { color: "#8B887F", dot: "#D5D2CA" },
};

function timeAgo(iso, lang) {
  if (!iso) return "—";
  const now = Date.now();
  const t = new Date(iso).getTime();
  const diff = Math.max(0, now - t);
  const m = Math.floor(diff / 60000);
  if (m < 1) return lang === "en" ? "just now" : "vừa xong";
  if (m < 60) return lang === "en" ? `${m}m ago` : `${m} phút trước`;
  const h = Math.floor(m / 60);
  if (h < 24) return lang === "en" ? `${h}h ago` : `${h} giờ trước`;
  const d = Math.floor(h / 24);
  return lang === "en" ? `${d}d ago` : `${d} ngày trước`;
}

function TopCampaigns({ campaigns, lang }) {
  const title = lang === "en" ? "Top Campaigns" : "Chiến Dịch Hiệu Quả Nhất";
  const subtitle = lang === "en"
    ? "Which channels are driving signups and attendance"
    : "Các kênh đang mang về đăng ký và check-in";

  const maxRegs = Math.max(1, ...(campaigns?.map((c) => c.registration_count) || [1]));
  const topBySignups = campaigns?.[0]?.source;
  const topByAttendance = (() => {
    const eligible = (campaigns || []).filter((c) => c.registration_count >= 3);
    if (!eligible.length) return null;
    return eligible.slice().sort((a, b) => b.attendance_rate - a.attendance_rate)[0];
  })();

  return (
    <section className="mt-8" data-testid="top-campaigns-section">
      <div className="flex items-end justify-between flex-wrap gap-2 mb-4">
        <div>
          <p className="text-[10px] uppercase tracking-[0.28em] text-espresso-muted font-semibold">
            {lang === "en" ? "Campaign performance" : "Hiệu quả chiến dịch"}
          </p>
          <h2 className="font-serif text-2xl md:text-3xl text-espresso leading-none mt-1">{title}</h2>
          <p className="text-espresso-soft text-sm mt-2">{subtitle}</p>
        </div>
        {topByAttendance && (
          <div className="hidden md:flex items-center gap-2 text-[11px] uppercase tracking-[0.22em] text-espresso-muted">
            <TrendingUp className="w-3.5 h-3.5 text-emerald2" />
            <span>{lang === "en" ? "Best attendance" : "Tỷ lệ tham dự cao nhất"}:</span>
            <span className="font-semibold text-espresso">{topByAttendance.label}</span>
            <span className="text-emerald2 font-semibold">{topByAttendance.attendance_rate}%</span>
          </div>
        )}
      </div>

      {(!campaigns || campaigns.length === 0) ? (
        <div className="bg-white border border-dashed border-espresso/20 rounded-3xl px-6 py-12 text-center" data-testid="campaigns-empty">
          <Trophy className="w-6 h-6 text-espresso-muted mx-auto" />
          <p className="mt-3 text-espresso-soft">
            {lang === "en"
              ? "No campaign data yet. Share your UTM links — Facebook, Instagram, QR codes, agent links — and signups will appear here."
              : "Chưa có dữ liệu chiến dịch. Chia sẻ link UTM (Facebook, Instagram, mã QR, link đại lý) — kết quả sẽ hiện tại đây."}
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
          {campaigns.map((c, i) => {
            const theme = CAMPAIGN_THEME[c.source] || CAMPAIGN_THEME.unknown;
            const isTop = c.source === topBySignups;
            const widthPct = Math.max(4, Math.round((c.registration_count / maxRegs) * 100));
            return (
              <div key={c.source} data-testid={`campaign-card-${c.source}`}
                className={`relative bg-white border rounded-3xl p-5 transition-all hover:-translate-y-0.5 ${isTop ? "border-gold-500/60 shadow-[0_18px_40px_-20px_rgba(201,161,74,0.5)]" : "border-espresso/10"}`}>
                {isTop && (
                  <div data-testid={`campaign-top-badge`} className="absolute -top-2.5 right-4 inline-flex items-center gap-1 bg-gold-500 text-charcoal rounded-full px-2.5 py-0.5 text-[10px] font-semibold uppercase tracking-wider">
                    <Trophy className="w-3 h-3" /> {lang === "en" ? "Top Source" : "Nguồn Tốt Nhất"}
                  </div>
                )}
                <div className="flex items-center justify-between gap-3">
                  <div className="flex items-center gap-2 min-w-0">
                    <span className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ background: theme.dot }} />
                    <div className="font-serif text-xl text-espresso truncate">{c.label}</div>
                  </div>
                  <div className="font-serif text-2xl text-espresso leading-none">{c.registration_count}</div>
                </div>

                <div className="mt-3 h-1.5 bg-warmgray-100 rounded-full overflow-hidden">
                  <div className="h-full rounded-full transition-all" style={{ width: `${widthPct}%`, background: theme.color }} />
                </div>

                <div className="mt-4 grid grid-cols-3 gap-2 text-center">
                  <Mini label={lang === "en" ? "Checked" : "Check-in"} value={c.checked_in_count} tone="success" />
                  <Mini label={lang === "en" ? "Walk-in" : "Walk-in"} value={c.walkin_count} />
                  <Mini label={lang === "en" ? "Attend" : "Tham dự"} value={`${c.attendance_rate}%`} tone={c.attendance_rate >= 60 ? "success" : c.attendance_rate >= 30 ? "warn" : "muted"} />
                </div>

                <div className="mt-3 text-[10px] uppercase tracking-[0.22em] text-espresso-muted font-semibold">
                  {lang === "en" ? "Last signup" : "Đăng ký gần nhất"}: <span className="text-espresso-soft normal-case tracking-normal">{timeAgo(c.latest_registration_at, lang)}</span>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </section>
  );
}

function Mini({ label, value, tone }) {
  const cls =
    tone === "success" ? "text-[#0E5B4F]" :
    tone === "warn" ? "text-[#8A5A19]" :
    "text-espresso";
  return (
    <div className="bg-warmgray-50 rounded-xl py-2">
      <div className={`font-serif text-lg leading-none ${cls}`}>{value}</div>
      <div className="text-[10px] uppercase tracking-[0.18em] text-espresso-muted mt-1 font-semibold">{label}</div>
    </div>
  );
}


// ─────────────────────────────────────────────────────────────────────────────
// Phase 8.4 — GIẤY TỜ column glyph. 7 states, shape + color distinguishable.
// ─────────────────────────────────────────────────────────────────────────────
function DocumentGlyph({ registration, onOpen }) {
  const lv = registration.license_verification || {};
  if (!lv.id) {
    return <span className="text-espresso-muted text-lg leading-none" title="Chưa tải hồ sơ" data-testid={`doc-glyph-${registration.id}-none`}>—</span>;
  }
  const ai = lv.ai_status;
  const admin = lv.admin_status;
  const extractionFailed = lv.ai_extracted_raw?.meta?.error && !lv.ai_extracted_owner_name;

  let icon, ring, label, testKey;
  if (admin === "admin_approved") {
    icon = <Check className="w-3.5 h-3.5" />;
    ring = "text-espresso-soft bg-white ring-1 ring-espresso/20";
    label = "Đã duyệt";
    testKey = "approved";
  } else if (admin === "admin_rejected") {
    icon = <XIcon className="w-3.5 h-3.5" />;
    ring = "text-espresso-soft bg-white ring-1 ring-espresso/20";
    label = "Đã từ chối";
    testKey = "rejected";
  } else if (admin === "awaiting_member") {
    icon = <Clock className="w-3.5 h-3.5" />;
    ring = "text-espresso-soft bg-white ring-1 ring-espresso/20";
    label = "Chờ member gửi lại";
    testKey = "awaiting";
  } else if (ai === "processing") {
    icon = <RefreshCw className="w-3.5 h-3.5 animate-spin" />;
    ring = "text-espresso-soft bg-white ring-1 ring-espresso/20";
    label = "Đang xử lý";
    testKey = "processing";
  } else if (extractionFailed) {
    icon = <AlertTriangle className="w-3.5 h-3.5" />;
    ring = "text-amber-800 bg-amber-100 ring-1 ring-amber-500";
    label = "Cần đọc thủ công";
    testKey = "extraction-failed";
  } else {
    // ai_needs_review — amber, on the to-do list
    icon = <span className="w-2 h-2 rounded-full bg-amber-500" />;
    ring = "text-amber-800 bg-amber-50 ring-1 ring-amber-400";
    label = "Cần duyệt";
    testKey = "needs-review";
  }

  return (
    <button
      type="button"
      onClick={onOpen}
      title={label}
      aria-label={label}
      data-testid={`doc-glyph-${registration.id}-${testKey}`}
      className="inline-flex items-center gap-1.5 rounded-full px-2 py-1 hover:bg-linen-100 transition-colors"
    >
      <Eye className="w-4 h-4 text-espresso" />
      <span className={`inline-flex items-center justify-center w-5 h-5 rounded-full ${ring}`}>
        {icon}
      </span>
    </button>
  );
}
